import React from 'react'
import { Swiper, SwiperClass, SwiperProps, SwiperSlide } from 'swiper/react'
import { FreeMode, Navigation } from 'swiper/modules'
import Image from 'components/Image'
import Paragraphs from 'components/home/common/typography/Paragraphs'
import { PropertyPageQuery } from 'gql/graphql'
import classNames from 'classnames'
import { useHasLoaded } from 'data/hooks/useHasLoaded'
import { defaultCarouselProps } from '@ama-selections/ui'

interface CarouselBedroomsBathroomsProps {
  bedrooms: NonNullable<PropertyPageQuery['property']>['bedroom_arrangements']
}

const CarouselBedroomsBathrooms = ({
  bedrooms = [],
}: CarouselBedroomsBathroomsProps) => {
  const { ref: componentRef, hasLoaded } = useHasLoaded()

  const navigationPrevRef = React.useRef(null)
  const navigationNextRef = React.useRef(null)
  const buttonClasses = 'absolute top-1/2 sm:invisible lg:visible -mt-25'

  const breakpoints: SwiperProps['breakpoints'] = {
    640: {
      spaceBetween: 40,
      freeMode: {
        enabled: true,
        sticky: true,
        momentum: true,
        momentumVelocityRatio: 0.25,
      },
    },
    960: {
      spaceBetween: 60,
    },
  }

  return (
    <div className="relative" ref={componentRef}>
      {
        hasLoaded && (
          <>
            <div ref={navigationPrevRef} className={classNames('swiper-button-prev left-0 xl:left-40', buttonClasses)} />
            <div ref={navigationNextRef} className={classNames('swiper-button-next right-0 xl:right-40', buttonClasses)} />

            <Swiper
              {...defaultCarouselProps}
              modules={[Navigation, FreeMode]}
              className="max-w-[980px]"
              slidesPerView={'auto'}
              spaceBetween={20}
              slidesOffsetAfter={25}
              slidesOffsetBefore={25}
              breakpoints={breakpoints}
              navigation={{
                prevEl: navigationPrevRef.current,
                nextEl: navigationNextRef.current,
              }}
              onSwiper={(swiper: SwiperClass) => {
                setTimeout(() => {
                  if (swiper.params.navigation && typeof(swiper.params.navigation) !== 'boolean') {
                  // Timeout until prevEl & nextEl refs are defined
                    swiper.params.navigation.prevEl = navigationPrevRef.current
                    swiper.params.navigation.nextEl = navigationNextRef.current
                  }

                  // Re-initialise navigation
                  swiper.navigation.destroy()
                  swiper.navigation.init()
                  swiper.navigation.update()
                })
              }}
            >
              <>
                {bedrooms != undefined
                  ? bedrooms.map((bedroomArrangement, index) => (
                    <SwiperSlide
                      className="h-[400px] w-[270px]"
                      key={index}
                    >
                      <div
                        className="flex flex-col items-center justify-center h-full py-20 antialiased border border-grey-650 tracking-bedroom"
                        key={bedroomArrangement.id}
                      >
                        {
                          bedroomArrangement.bedroom_type[0]?.media?.url && (
                            <div className="mb-20">
                              <Image
                                src={bedroomArrangement.bedroom_type[0]?.media?.url}
                                width={70}
                                height={70}
                              />
                            </div>
                          )
                        }

                        <Paragraphs.PropertyPage className="font-bold uppercase text-grey-800">
                          Bedroom {index + 1}
                        </Paragraphs.PropertyPage>

                        <div className="mt-[24px] text-grey-500 w-full text-center">
                          <div className="flex flex-col">
                            {bedroomArrangement.bedroom_type.map((type) => (
                              <Paragraphs.PropertyPage key={type.id}>{type.value}</Paragraphs.PropertyPage>
                            ))}
                            {bedroomArrangement.bedroom_ac_type.map((type) => (
                              <Paragraphs.PropertyPage key={type.id}>{type.value}</Paragraphs.PropertyPage>
                            ))}
                            {bedroomArrangement.bedroom_outdoor_space.map((type) => (
                              <Paragraphs.PropertyPage key={type.id}>{type.value}</Paragraphs.PropertyPage>
                            ))}
                          </div>
                          <div className="flex flex-col mt-[23px]">
                            {bedroomArrangement.bedroom_wash_facility.map((type) => (
                              <Paragraphs.PropertyPage key={type.id}>{type.value} Bathroom:</Paragraphs.PropertyPage>
                            ))}
                            {bedroomArrangement.bedroom_toilet_access.map((type) => (
                              <Paragraphs.PropertyPage key={type.id}>{type.value}</Paragraphs.PropertyPage>
                            ))}
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  ))
                  : null}
              </>
            </Swiper>
          </>
        )}
    </div>

  )
}

export default CarouselBedroomsBathrooms
